/** useCoPilotGuide — composes session, message, and feature hooks with action handlers, navigation, and lifecycle. */

import { CoPilotGuideEngine, type Action, type Chat, type TransitionResult } from '@tonnie/copilot';
import { useTranslation } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';
import { safeParseChatMessages } from '@tonnie/schemas';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clearSessionState, loadSessionState } from '../guide-session';
import type { IAction, IMessage } from '../types';
import type {
  GuideAnalyticsEventType,
  GuideTraceEventType,
  GuideTraceEvent,
  ICoPilotGuideProps,
  InstantiationState,
} from '../copilot-guide.types';
import { useCopilotLogger } from '../../../lib/use-copilot-logger';
import { useGuideSession } from './use-guide-session';
import { useGuideMessages } from './use-guide-messages';
import { useGuideFeatures } from './use-guide-features';

export function useCoPilotGuide({
  config,
  initialVariables = {},
  onComplete,
  onExit,
  onProgressChange,
  onChatChange,
  onAnalyticsEvent,
  onTraceEvent,
  onRestart: onRestartProp,
  onRestoreError,
  onInstantiate,
  persistSession = true,
  journeyId,
  persistFlowId,
}: Omit<ICoPilotGuideProps, 'className'>) {
  const { t } = useTranslation();
  const { captureException } = useErrorCapture();

  // ── Mounted ref + timeout tracking ──
  const mountedRef = useRef(true);
  const timeoutIds = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Tracks whether the start/restore effect has already kicked off a chat
  // chain this mount, plus the lifecycle signature it last acted on, so a
  // benign `serverGuideState` materialization (undefined → 0 counters) does
  // not re-trigger a second concurrent chain. See the effect below.
  const hasStartedRef = useRef(false);
  const lastLifecycleSigRef = useRef('');
  const safeTimeout = useCallback((fn: () => void, ms: number) => {
    const id = setTimeout(() => {
      timeoutIds.current = timeoutIds.current.filter((t) => t !== id);
      if (mountedRef.current) fn();
    }, ms);
    timeoutIds.current.push(id);
    return id;
  }, []);

  // ── Engine singleton ──
  // `flowId` drives BOTH localStorage keying AND server persistence.
  // The host MUST pass `persistFlowId` matching a key in the lifecycle
  // registry; we runtime-assert this to fail loud rather than silently
  // splitting state across two `copilot_guide_states` rows.
  if (!persistFlowId) {
    const message =
      '[CoPilotGuide] persistFlowId is required. Pass the same stable key your <FlowLifecycleManager> uses (e.g. "checkup", "care_assessment", "qultureup", "teleportal_setup", "journey_builder").';
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(message);
    }
    captureException(new Error(message), { extra: { configId: config?.id } });
  }
  const flowId = persistFlowId ?? config?.id;
  const log = useCopilotLogger('CP:Engine', { flowId, journeyId });
  const [engine] = useState(() => {
    log.debug('instantiate', {
      data: {
        configId: config?.id,
        initialVariableKeys: Object.keys(initialVariables ?? {}),
      },
    });
    const e = new CoPilotGuideEngine(config);
    Object.entries(initialVariables).forEach(([k, v]) => {
      e.setVariable(k, v);
    });
    return e;
  });

  // ── Core state ──
  const [currentChat, setCurrentChat] = useState<Chat | null>(null);
  const [chatMessages, setChatMessages] = useState<IMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [instantiationState, setInstantiationState] = useState<InstantiationState | null>(null);
  const [actions, setActions] = useState<Action[]>([]);
  const [currentFeature, setCurrentFeature] = useState<{
    chatId: string;
    feature: string;
    config: Record<string, unknown>;
    displayMode: 'blended' | 'modal';
  } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [canGoBack, setCanGoBack] = useState(false);
  const processChatRef = useRef<(chat: Chat) => void>(null);
  const traceSeqRef = useRef(0);

  // ── Analytics + Trace helpers ──
  const emitEvent = useCallback(
    (type: GuideAnalyticsEventType, meta?: Record<string, unknown>) => {
      onAnalyticsEvent?.({
        type,
        flowId,
        progress: engine.getCompletionPercentage(),
        timestamp: Date.now(),
        ...meta,
      });
    },
    [onAnalyticsEvent, flowId, engine]
  );
  const onTraceEventRef = useRef(onTraceEvent);
  onTraceEventRef.current = onTraceEvent;
  const emitTrace = useCallback(
    (
      type: GuideTraceEventType,
      extra?: Partial<Omit<GuideTraceEvent, 'id' | 'type' | 'timestamp'>>
    ) => {
      onTraceEventRef.current?.({
        id: `t_${++traceSeqRef.current}_${Date.now()}`,
        type,
        timestamp: Date.now(),
        ...extra,
      });
    },
    []
  );
  const updateProgress = useCallback(() => {
    onProgressChange?.(engine.getCompletionPercentage());
  }, [engine, onProgressChange]);

  // ── Session persistence ──
  const { serverGuideState, guideStateLoading, saveTimerRef, markFlowCompleted, saveResetState } =
    useGuideSession(flowId, persistSession, engine, chatMessages, journeyId);

  // Mirror the action-handler's sentinel logic so an `auto_transition`
  // that targets `terminal` / `escape` actually closes the flow.
  // Without this, `engine.transition('terminal', ...)` returns
  // `{shouldClose: true, shouldRedirect: '<x>', nextChatId: null}` and
  // the auto-transition handler in `use-guide-messages.ts` only
  // checked `nextChatId` — silently dropping the close on the floor.
  // The user would sit on the chat forever staring at "Redirecting…"
  // text while nothing redirected. Wiring this callback closes the
  // gap; the action-handler path on line ~563 already had its own
  // copy of this logic for button-driven sentinel transitions.
  const handleSentinelTransition = useCallback(
    (result: TransitionResult) => {
      if (!result.shouldClose) return;
      // Route by lifecycle kind, NOT by redirect target. `terminal` is
      // a natural completion (run finalize); `escape` is a mid-flow
      // bail (no finalize, just navigate). The redirect target can be
      // /dashboard in either case — using it as the routing predicate
      // (the previous bug) silently dropped finalize on every
      // terminal-with-dashboard-redirect, which is exactly what the
      // journey-builder's primary CTA is.
      if (result.transitionKind === 'escape' && onExit) {
        clearSessionState(flowId, journeyId ?? null);
        emitEvent('flow_exited');
        emitTrace('flow_exited', { data: { redirect: result.shouldRedirect ?? 'dashboard' } });
        onExit();
        return;
      }
      if (onComplete) {
        markFlowCompleted();
        clearSessionState(flowId, journeyId ?? null);
        // The host's `onComplete` path runs `useGuideHostFinalize` which
        // atomically writes the final state + widget snapshot + flips
        // status='completed' in a single DB transaction. Calling
        // `saveCompletedState()` here in addition fired a SECOND parallel
        // mutation against the same row, both reading the same cached
        // rowVersion → `expected=N, actual=N+1` conflict on whichever
        // landed second. That race is exactly what produced the
        // "Could not save your results" cascade after recap_summary
        // submit (radar 2026-05-08). Finalize is the single canonical
        // writer; no shadow save here.
        emitEvent('flow_completed', { metadata: { redirect: result.shouldRedirect } });
        emitTrace('flow_completed', {
          data: { redirect: result.shouldRedirect, variables: engine.getAllVariables() },
        });
        onComplete({ redirect: result.shouldRedirect, ...engine.getAllVariables() });
      }
    },
    [onExit, onComplete, flowId, journeyId, emitEvent, emitTrace, engine, markFlowCompleted]
  );

  // ── Message processing ──
  const { processChat, processEmbeddedFeature } = useGuideMessages({
    engine,
    mountedRef,
    safeTimeout,
    setChatMessages,
    setIsLoading,
    setIsAnimating,
    setActions,
    setAnnouncement,
    setInstantiationState,
    setCurrentChat,
    setCurrentFeature,
    processChatRef,
    emitEvent,
    emitTrace,
    updateProgress,
    onChatChange,
    onInstantiate,
    onSentinelTransition: handleSentinelTransition,
    initialVariables,
  });
  useEffect(() => {
    processChatRef.current = processChat;
  }, [processChat]);

  // ── Feature handlers ──
  const { handleFeatureComplete, handleFeatureCancel } = useGuideFeatures({
    engine,
    flowId,
    journeyId: journeyId ?? null,
    currentFeature,
    safeTimeout,
    setChatMessages,
    setActions,
    setCurrentChat,
    setCurrentFeature,
    setAnnouncement,
    emitEvent,
    emitTrace,
    updateProgress,
    processChat,
    onComplete,
    markFlowCompleted,
  });

  // ── Mount / Unmount lifecycle ──
  useEffect(() => {
    mountedRef.current = true;

    log.debug('mount');
    return () => {
      mountedRef.current = false;
      timeoutIds.current.forEach(clearTimeout);
      timeoutIds.current = [];
      // Scope the "already started" flag to this mount. React StrictMode
      // (dev) runs setup→cleanup→setup; without this reset the remount's
      // start-effect would see `hasStartedRef === true` and skip emitting
      // the first chat, leaving the guide blank. A real unmount resets it
      // too, which is correct — the next mount must start fresh.
      hasStartedRef.current = false;
      lastLifecycleSigRef.current = '';

      log.debug('unmount');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Setup restored chat ──
  const setupRestoredChat = useCallback(
    (restoredChat: Chat, source: 'server' | 'localStorage') => {
      setCurrentChat(restoredChat);
      if (restoredChat.type === 'embeddedFeature') {
        processEmbeddedFeature(restoredChat);
      } else if (restoredChat.metadata?.next_is_feature) {
        const task = engine.getCurrentTask();
        if (task) {
          const idx = task.chats.findIndex((c) => c.id === restoredChat.id);
          const next = task.chats[idx + 1];
          if (next && next.type === 'embeddedFeature') {
            engine.completeChat(restoredChat.id);
            engine.transition(next.id);
            setCurrentChat(next);
            processEmbeddedFeature(next);
          }
        }
      } else if (
        restoredChat.type === 'messageSequence' &&
        ((Array.isArray(restoredChat.metadata?.effects) &&
          (restoredChat.metadata?.effects as string[]).includes('call_instantiation')) ||
          restoredChat.metadata?.auto_transition)
      ) {
        // The chat's only path forward lives in `processChat`'s
        // post-message block — `call_instantiation` (re-fires the
        // brain mutation, opens the Continue card) or `auto_transition`
        // (timer that hops to the next chat). Without re-running
        // `processChat` on restore, a user who navigated away while
        // sitting on `processing_start` (TelePortal Setup's "Building
        // your Relocation Kit" step) lands back on a chat with no UI
        // affordance to continue: `instantiationState` is React-local
        // so the previous "kit ready, Continue" card is gone, and
        // nothing schedules the auto-transition. The flow goes mute.
        // Re-run `processChat` — message dedup inside `processNext`
        // prevents duplicate bubbles, and the mutation is idempotent
        // for the same setup variables.
        log.info('restore.reenterMessageSequence', {
          chatId: restoredChat.id,
          data: {
            hasInstantiation:
              Array.isArray(restoredChat.metadata?.effects) &&
              (restoredChat.metadata?.effects as string[]).includes('call_instantiation'),
            autoTransition: restoredChat.metadata?.auto_transition ?? null,
          },
        });
        processChatRef.current?.(restoredChat);
      } else if (restoredChat.actions && restoredChat.actions.length > 0) {
        setActions(restoredChat.actions);
      }
      safeTimeout(updateProgress, 100);
      emitEvent('session_restored', { chatId: restoredChat.id });
      emitTrace('session_restored', {
        chatId: restoredChat.id,
        data: { source, variables: engine.getState().variables },
      });
    },
    [engine, processEmbeddedFeature, safeTimeout, updateProgress, emitEvent, emitTrace, log]
  );

  // ── Start flow (with session restore) ──
  useEffect(() => {
    if (guideStateLoading) {
      log.debug('restore.waiting');
      return;
    }

    log.debug('restore.fired', {
      data: {
        persistSession,
        serverGuideState: serverGuideState
          ? {
              status: serverGuideState.status,
              currentChatId: serverGuideState.currentChatId,
              completedChats: serverGuideState.completedChats?.length ?? 0,
              progress: serverGuideState.progress,
            }
          : null,
      },
    });

    // ── Suppress benign re-fires ──────────────────────────────────────
    // This effect's deps include `serverGuideState?.restartCount` /
    // `?.retakeCount`. On a fresh flow the guide-state query first returns
    // `null` (so both read `undefined`) and we fresh-start, emitting the
    // welcome chat. That fresh-start autosaves a row server-side; the query
    // then refetches and the row arrives with `restartCount = retakeCount =
    // 0`, flipping the deps `undefined → 0`. That is NOT a restart/retake —
    // but it re-runs this effect, which would `engine.reset()` + re-emit the
    // SAME chat, racing the in-flight typing chain (root cause of a bubble
    // stranded `isTyping: true` whose action button never renders). Only
    // act when the lifecycle signature genuinely changes (a real restart or
    // retake increments a counter). Normalizing `?? 0` makes undefined and 0
    // identical, so the materialization is a no-op here.
    const lifecycleSig = `${serverGuideState?.restartCount ?? 0}:${serverGuideState?.retakeCount ?? 0}`;
    if (hasStartedRef.current && lifecycleSig === lastLifecycleSigRef.current) {
      log.debug('restore.skip-benign-refire', { data: { lifecycleSig } });
      return;
    }
    hasStartedRef.current = true;
    lastLifecycleSigRef.current = lifecycleSig;

    setChatMessages([]);
    setActions([]);
    setIsAnimating(false);
    setIsLoading(false);
    setCurrentFeature(null);
    setCurrentChat(null);

    // ── Pure restore-or-fresh-start ──────────────────────────────────
    // The engine is now a dumb chat-history viewer. The hero's
    // assessment table (e.g. checkup_assessments) is the canonical
    // state machine; lifecycle decisions (cancel / restart / retake)
    // are owned by the host pages, which run mutations server-side
    // that clear `currentChatId` BEFORE the user lands here.
    //
    // Policy:
    //   1. If the server has a `currentChatId` for this flow+journey,
    //      restore that chat history. This is the "user came back,
    //      pick up where they left off" path.
    //   2. Else if localStorage has a usable chat, restore that
    //      (covers offline-write-then-online edge cases).
    //   3. Else fresh-start: engine.start() emits the welcome chat.
    //
    // The previous `serverIsTerminal` bypass-restore branch — which
    // silently flipped status='cancelled' rows back to 'active' via
    // saveResetState and synthesized intent the user never gave —
    // has been removed. Hosts now route users away from the
    // assessment URL when the assessment table says they shouldn't
    // be here, so this code path can trust the URL.
    const localState = loadSessionState(flowId, journeyId ?? null);
    const serverHasResumable = !!serverGuideState && !!serverGuideState.currentChatId;

    if (persistSession && serverHasResumable && serverGuideState) {
      // Server is the canonical source of truth for the rendered
      // conversation. Read `chatMessages` from the server first; fall
      // back to localStorage only if the server has none AND
      // localStorage does (covers a race where a user was offline-
      // saving locally then came back online for the first time).
      // Pre-2026-04-27 this fell back unconditionally to localStorage,
      // which meant a clean tab / different device showed a blank
      // chat panel for an in-progress assessment.
      // Validate the server-returned UI bubbles against the canonical
      // wire schema (`@tonnie/schemas`). Replaces the previous
      // `as unknown as IMessage[]` cast (radar 2026-04-28 — C-3 / M-9):
      // if the server payload drifts from the contract, we log + fall
      // back to an empty array instead of crashing on render with a
      // missing `timestamp.toLocaleTimeString()` etc.
      //
      // Returns `ChatMessagesWire` (`timestamp: string | Date`); the
      // hydrate-to-`IMessage` step happens below, where each entry is
      // converted to a real `Date` before going into render state.
      const serverChatMessages = safeParseChatMessages(serverGuideState.chatMessages);
      log.info('restore.server.start', {
        chatId: serverGuideState.currentChatId ?? undefined,
        data: {
          completedChats: serverGuideState.completedChats?.length ?? 0,
          status: serverGuideState.status,
          serverChatMessages: serverChatMessages.length,
        },
      });
      try {
        engine.restoreState({
          currentTaskId: serverGuideState.currentTaskId ?? null,
          currentChatId: serverGuideState.currentChatId ?? null,
          completedTasks: new Set(serverGuideState.completedTasks ?? []),
          completedChats: new Set(serverGuideState.completedChats ?? []),
          variables: (serverGuideState.variables ?? {}) as Record<string, unknown>,
          chatHistory: (serverGuideState.chatHistory ?? []).map((e) => ({
            ...e,
            timestamp: new Date(e.timestamp),
            type: e.type as 'message' | 'feature' | 'action',
          })),
          // Seed widget answers atomically with the rest of the state so
          // any embeddedFeature chat we restore into renders with the
          // user's prior answers on first paint — no flicker.
          widgetStates: serverGuideState.widgetStates ?? {},
        });
        if (serverChatMessages.length > 0) {
          // Hydrate `Date` instances on timestamp fields — the server
          // returns ISO strings (JSON has no Date type) but the chat
          // bubble components render `timestamp.toLocaleTimeString()`,
          // which would throw on a string.
          const hydrated: IMessage[] = serverChatMessages.map((m) => ({
            ...m,
            timestamp: typeof m.timestamp === 'string' ? new Date(m.timestamp) : m.timestamp,
          }));
          setChatMessages(hydrated);
        } else if (localState) {
          // Server has no UI bubbles yet (older row written before this
          // column shipped, or the save raced ahead of the bubble
          // persistence). Fall back to localStorage as a hint.
          setChatMessages(localState.messages);
        }
        const restoredChat = engine.getCurrentChat();
        if (restoredChat) {
          const restoredTask = engine.getCurrentTask();
          if (restoredTask?.type === 'Terminal' || restoredTask?.type === 'Escape') {
            // Restored into a terminal task — this is a degenerate
            // server state. Fall through to fresh-start so the user
            // sees the welcome chat instead of being stranded on a
            // task with no actions.
            log.info('restore.server.terminal');
            clearSessionState(flowId, journeyId ?? null);
          } else {
            log.info('restore.server.ok', {
              chatId: restoredChat.id,
              data: { chatMessages: serverChatMessages.length },
            });
            setupRestoredChat(restoredChat, 'server');
            return;
          }
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        log.error('restore.server.threw', { data: { source: 'server' } }, { error });
        // Surface the error to the host (toast / banner / modal). The
        // fallback path below still fresh-starts so the user is never
        // stranded — but they're now informed that their previous
        // session couldn't be loaded, instead of silently losing it.
        // Radar 2026-04-28 — M-2.
        captureException(error, {
          component: 'CoPilotGuide',
          action: 'restoreState',
          extra: { flowId, journeyId, source: 'server' },
        });
        try {
          onRestoreError?.(error, { source: 'server' });
        } catch {
          // A misbehaving host callback must not block the user.
          log.warn('restore.callback.threw', {
            data: { which: 'onRestoreError', source: 'server' },
          });
        }
      }
    } else if (persistSession && serverGuideState && !serverHasResumable) {
      log.debug('restore.server.skip', {
        data: { engineVarKeys: Object.keys(engine.getAllVariables() ?? {}) },
      });
    }

    // ── localStorage fallback policy (radar 2026-05-08) ───────────────
    //
    // localStorage restore is the "user wrote offline, came back online,
    // server hasn't seen the writes yet" path — a SUPPLEMENT to a
    // server that has no record of the flow. It must NOT override a
    // server that has a row with currentChatId=null.
    //
    // Pre-fix: the engine fell through to localStorage whenever
    // `serverHasResumable` was false, even when the server explicitly
    // returned a row with currentChatId=null (admin SQL reset, the
    // discard/restart/retake mutations, race against an in-flight
    // mutation). Result: the FE would happily restore stale chat
    // history from localStorage and skip past steps the server's
    // CheckUp/Care/QultureUp row needed re-collected — the user
    // walked back into the assessment, my mount-time auto-advance
    // skipped past completed widgets, and the new
    // `checkup_assessments` row never got `videos_watched` /
    // `pitch_video` populated. End state: flow looked finished on the
    // FE but `canCalculate=false` server-side, leaving "Continue
    // Assessment" forever.
    //
    // The right policy: only consult localStorage when the server has
    // NO ROW at all (genuinely first-time on this device, or offline).
    // If a row exists with currentChatId=null, the server is saying
    // "fresh-start" and localStorage MUST be cleared so it can't
    // poison the next mount.
    const serverExplicitlyReset =
      persistSession && !!serverGuideState && !serverGuideState.currentChatId;
    if (serverExplicitlyReset && localState) {
      log.info('restore.localStorage.skip.serverReset', {
        data: {
          serverStatus: serverGuideState?.status,
          localChatId: localState.engineState.currentChatId,
        },
      });
      clearSessionState(flowId, journeyId ?? null);
    }

    if (
      !serverExplicitlyReset &&
      persistSession &&
      localState &&
      localState.engineState.currentChatId
    ) {
      log.info('restore.localStorage.start', {
        chatId: localState.engineState.currentChatId,
        data: { msgs: localState.messages.length },
      });
      try {
        engine.restoreState({
          currentTaskId: localState.engineState.currentTaskId,
          currentChatId: localState.engineState.currentChatId,
          completedTasks: new Set(localState.engineState.completedTasks),
          completedChats: new Set(localState.engineState.completedChats),
          variables: localState.engineState.variables,
        });
        setChatMessages(localState.messages);
        const restoredChat = engine.getCurrentChat();
        if (restoredChat) {
          log.info('restore.localStorage.ok', { chatId: restoredChat.id });
          setupRestoredChat(restoredChat, 'localStorage');
          return;
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        log.error('restore.localStorage.threw', { data: { source: 'localStorage' } }, { error });
        captureException(error, {
          component: 'CoPilotGuide',
          action: 'restoreState',
          extra: { flowId, journeyId, source: 'localStorage' },
        });
        try {
          onRestoreError?.(error, { source: 'localStorage' });
        } catch {
          log.warn('restore.callback.threw', {
            data: { which: 'onRestoreError', source: 'localStorage' },
          });
        }
        clearSessionState(flowId, journeyId ?? null);
      }
    }

    log.info('restore.fresh-start');
    // Reset engine internal state BEFORE re-starting. The effect can
    // re-fire after a server restart/retake (see deps below), which
    // means the engine instance carries chatHistory + completedTasks
    // from the prior run. `engine.start()` alone doesn't wipe that —
    // calling reset + re-applying initialVariables gives us the same
    // clean slate the standalone `handleRestart` callback produces.
    engine.reset();
    Object.entries(initialVariables).forEach(([k, v]) => {
      engine.setVariable(k, v);
    });
    const firstChat = engine.start();
    if (firstChat) {
      log.info('fresh-start.firstChat', { chatId: firstChat.id });
      setCurrentChat(firstChat);
      processChat(firstChat);
      safeTimeout(updateProgress, 100);
      emitEvent('flow_started');
      emitTrace('flow_started', { chatId: firstChat.id });
    }
    // Re-run the effect when the server signals a destructive
    // lifecycle event happened (restart / retake bump these
    // counters). Other refetches (autosave heartbeats, widget patches)
    // change the row's `updatedAt` but NOT these counters, so the
    // effect stays idle during normal use.
    //
    // Without this, after `useRestartGuide` invalidates the cache,
    // the new `serverGuideState` (with currentChatId=null) lands in
    // the cache but the effect doesn't observe it — the engine keeps
    // showing the pre-restart chat. Adding `restartCount` /
    // `retakeCount` to the deps makes the engine react to the
    // server's "this flow was just reset" signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideStateLoading, serverGuideState?.restartCount, serverGuideState?.retakeCount]);

  // ── Brain processing card callbacks ──
  const handleInstantiationContinue = useCallback(() => {
    if (!instantiationState) return;
    const { chatId, autoTransition } = instantiationState;
    setInstantiationState(null);
    setIsLoading(false);
    let result = engine.completeChat(chatId);
    if (!result && autoTransition) result = engine.transition(autoTransition);
    if (result?.nextChatId || autoTransition) {
      const next = engine.getCurrentChat();
      if (next) {
        setCurrentChat(next);
        onChatChange?.(next.id, engine.getState().variables);
        emitEvent('chat_entered', { chatId: next.id });
        processChatRef.current?.(next);
        safeTimeout(updateProgress, 100);
      }
    }
  }, [instantiationState, engine, onChatChange, emitEvent, safeTimeout, updateProgress]);

  const handleInstantiationRetry = useCallback(() => {
    if (!instantiationState || !currentChat) return;
    setInstantiationState(null);
    setActions([]);
    processChat(currentChat);
  }, [instantiationState, currentChat, processChat]);

  // ── Action handler ──
  const handleAction = useCallback(
    (action: Action) => {
      log.info('action.selected', {
        chatId: currentChat?.id,
        data: {
          actionId: action.id,
          completedChatsBefore: engine.getState().completedChats.size,
        },
      });
      if (action.id === '__retry_instantiation' && currentChat) {
        setActions([]);
        processChat(currentChat);
        return;
      }
      const displayText = 'text' in action && action.text ? action.text : '...';
      setChatMessages((prev) => [
        ...prev,
        {
          id: `user_${Date.now()}`,
          type: 'user',
          content: displayText,
          timestamp: new Date(),
          isTyping: false,
        },
      ]);
      setActions([]);
      setIsLoading(true);
      emitEvent('action_selected', { actionId: action.id, chatId: currentChat?.id });
      emitTrace('action_selected', {
        actionId: action.id,
        chatId: currentChat?.id ?? undefined,
        data: { text: displayText, transition: action.transition },
      });
      if (currentChat?.id) engine.completeChat(currentChat.id);
      if (action.condition && !engine.evaluateCondition(action.condition)) return;
      if (action.transition) {
        const effectsArray = Array.isArray(action.effects) ? action.effects : undefined;

        // Effect → widget-completion bridge.
        //
        // Some CoPilot effects defined in the brain config are *intended* to
        // mark a widget as complete without the user actually interacting
        // with that widget (e.g. the "Skip to next step" branch in the
        // CheckUp assessment marks all videos as watched because the user
        // self-attests familiarity). The brain engine's `processEffects`
        // mutates internal state (`state.variables`, `state.completedTasks`)
        // but does NOT persist anything to the backend on its own — that
        // path is intentionally pull-based via widget events.
        //
        // Without this bridge, the user-approved skip path is a silent
        // no-op for downstream consumers like CheckUp's `videosWatched`
        // progress flag — the assessment status never flips to
        // `ready_to_calculate` and the Calculate button never appears.
        // Surface the effect as a `widget_completed` analytics event so
        // the host (CheckUpAssessmentPage's `handleAnalyticsEvent`) can
        // POST `widgetCompletions.video_carousel = { completed: true }`
        // through the existing reportWidgetProgress mutation.
        //
        // This is additive: any host that doesn't care about the event
        // already ignores the channel, and any future effect → widget
        // mapping can be added below.
        if (effectsArray?.includes('mark_all_videos_watched')) {
          emitEvent('widget_completed', {
            widgetName: 'video_carousel',
            chatId: currentChat?.id,
          });
        }

        const result = engine.transition(action.transition, effectsArray);
        if (result.shouldClose) {
          // Route by lifecycle kind — see `handleSentinelTransition`
          // for the full rationale. Same fix applied in both places
          // because the engine has two paths (sentinel auto-transition
          // and explicit-action transition) that resolve to the same
          // close.
          if (result.transitionKind === 'escape' && onExit) {
            clearSessionState(flowId, journeyId ?? null);
            emitEvent('flow_exited');
            emitTrace('flow_exited', { data: { redirect: result.shouldRedirect ?? 'dashboard' } });
            onExit();
          } else if (onComplete) {
            markFlowCompleted();
            clearSessionState(flowId, journeyId ?? null);
            // No `saveCompletedState()` here — see the parallel branch in
            // `handleSentinelTransition` for the rationale. Finalize, run
            // by the host's `onComplete` via `useGuideHostFinalize`, is
            // the single canonical writer for status='completed'.
            emitEvent('flow_completed', { metadata: { redirect: result.shouldRedirect } });
            emitTrace('flow_completed', {
              data: { redirect: result.shouldRedirect, variables: engine.getAllVariables() },
            });
            onComplete({ redirect: result.shouldRedirect, ...engine.getAllVariables() });
          }
          return;
        }
        if (result.nextChatId) {
          const next = engine.getCurrentChat();
          if (next) {
            setCurrentChat(next);
            processChat(next);
            safeTimeout(updateProgress, 100);
          }
        }
      }
    },
    [
      engine,
      processChat,
      onExit,
      onComplete,
      updateProgress,
      safeTimeout,
      emitEvent,
      emitTrace,
      currentChat,
      flowId,
      journeyId,
      log,
      markFlowCompleted,
    ]
  );

  // ── Chat action adapters ──
  const chatActions: IAction[] = useMemo(
    () =>
      actions.map((a) => ({
        id: a.id,
        type: a.type as IAction['type'],
        text: a.type === 'text_input' ? undefined : (a as { text: string }).text,
        placeholder:
          a.type === 'text_input' ? (a as { placeholder?: string }).placeholder : undefined,
        dataPointId:
          a.type === 'text_input' ? (a as { dataPointId: string }).dataPointId : undefined,
        style: a.style === 'primary' ? ('primary' as const) : ('secondary' as const),
        transition: a.transition,
        effects: a.effects ? Object.fromEntries(a.effects.map((e) => [e, true])) : undefined,
        condition: a.condition,
      })),
    [actions]
  );
  const handleChatAction = useCallback(
    (action: IAction) => {
      if (action.type === 'text_input' && action.dataPointId && action.text) {
        engine.setVariable(action.dataPointId, action.text);
        engine.setVariable('specialCircumstances', { notes: action.text, items: [action.text] });
      }
      const engineAction = actions.find((a) => a.id === action.id);
      if (engineAction) {
        handleAction(
          action.text ? ({ ...engineAction, text: action.text } as Action) : engineAction
        );
      }
    },
    [actions, handleAction, engine]
  );

  // ── Back navigation ──
  const handleGoBack = useCallback(() => {
    const result = engine.goBack();
    setCanGoBack(engine.canGoBack());
    if (result.nextChatId) {
      const chat = engine.getCurrentChat();
      if (chat) {
        setCurrentFeature(null);
        setActions([]);
        setIsAnimating(false);
        setCurrentChat(chat);
        processChat(chat);
      }
    }
  }, [engine, processChat]);
  useEffect(() => {
    setCanGoBack(engine.canGoBack());
  }, [currentChat, engine]);

  // Clear `currentFeature` whenever the chat moves away from the chat
  // that owned it. Without this guard, an auto-transition or a
  // back-nav could leave a stale embedded-feature widget context
  // alive — the next render would briefly show the wrong widget.
  // Radar 2026-04-28 — m-3.
  useEffect(() => {
    if (!currentChat || !currentFeature) return;
    if (currentFeature.chatId !== currentChat.id) {
      setCurrentFeature(null);
    }
  }, [currentChat, currentFeature]);

  // ── Restart flow ──
  const handleRestart = useCallback(() => {
    clearSessionState(flowId, journeyId ?? null);
    timeoutIds.current.forEach(clearTimeout);
    timeoutIds.current = [];
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    engine.reset();
    Object.entries(initialVariables).forEach(([k, v]) => {
      engine.setVariable(k, v);
    });
    saveResetState(initialVariables);
    setChatMessages([]);
    setActions([]);
    setCurrentFeature(null);
    setIsAnimating(false);
    setIsLoading(false);
    setInstantiationState(null);
    setCanGoBack(false);
    setAnnouncement('');
    onRestartProp?.();
    const firstChat = engine.start();
    if (firstChat) {
      setCurrentChat(firstChat);
      processChat(firstChat);
      safeTimeout(updateProgress, 100);
      emitEvent('flow_started');
    }
  }, [
    engine,
    flowId,
    journeyId,
    initialVariables,
    processChat,
    safeTimeout,
    updateProgress,
    emitEvent,
    onRestartProp,
    saveResetState,
    saveTimerRef,
  ]);

  // ── Keyboard navigation ──
  // Escape is suppressed while an action is in-flight (`isLoading`)
  // or a chat animation is mid-typing (`isAnimating`). Without the
  // guard, a user mashing Escape during a long brain-instantiation
  // would exit mid-call and the next mount would land on the post-
  // instantiation chat with no widget — a confusing dead end. The
  // user can still escape via the Cancel footer button, which
  // routes through the lifecycle manager's discard modal. Radar
  // 2026-04-28 — m-2.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && onExit) {
        if (isLoading || isAnimating) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        onExit();
      }
    },
    [onExit, isLoading, isAnimating]
  );

  return {
    engine,
    currentChat,
    chatMessages,
    isLoading,
    isAnimating,
    instantiationState,
    currentFeature,
    announcement,
    canGoBack,
    chatActions,
    handleChatAction,
    handleGoBack,
    handleRestart,
    handleKeyDown,
    handleInstantiationContinue,
    handleInstantiationRetry,
    handleFeatureComplete,
    handleFeatureCancel,
    t,
    captureException,
  };
}
